import { EasingFunction } from '@babylonjs/core/Animations/easing';
import { Vector2 } from '@babylonjs/core/Maths/math';

import { MeshSystem } from '../System/meshSystem';
import { GravityGrid } from '../System/gravityGrid';
import { StarDust } from '../Objects/starDust';
import { Player } from '../Player/player';
import { BlackHole } from '../Objects/blackHole';
import { Star, StarInterface } from '../Objects/star';
import { PlanetMap } from './planetMap';

/**
 * Manage all the essential assets needed to build a 3D scene (Engine, Scene Cameras, etc)
 *
 * The system is really important as it is often sent in every other class created to manage core assets
 */

export class TileMap extends PlanetMap {

    gravityGrid: GravityGrid;
    curve: EasingFunction;

    constructor(system: MeshSystem, gravityGrid: GravityGrid) {
        super(system);
        this.gravityGrid = gravityGrid;
        
        let frame = 0;
        this.system.scene.registerBeforeRender(() => {
            this.checkPlanetsCycle();
            this.checkPlayersCycle();
            if (frame > 10 && this.check) {
                this.checkPlayersDust();
                this.checkDustMap();
                this.checkUpperDustMap();
                frame = 0;
            }
            frame++;
        });
    }

    check = false;
    checkPlayerAndRessources(check: boolean) {
        this.check = check;
    }
    
    playerToFollow: Player;
    setPlayerToFollow(player: Player) {
        this.playerToFollow = player;
    }

    disposePlayer(player: Player) {
        this.removeStar(player);
        player.dispose();
    }

    ////////// STAR

    createStar(starInterface: StarInterface) {
        let player = new Player(this.system, this.gravityGrid, this, starInterface);
        player.key = starInterface.key;
        this.addStar(player);
        return player;
    }

    stars: Object = {};
    addStar(star: Star) {
        this.stars[star.key] = star;
    }

    removeStar(star: Star) {
        delete this.stars[star.key];
    }

    eraseAllStars() {
        for (const key in this.stars) {
            let star = this.stars[key];
            if (star != this.playerToFollow) star.dispose();
        }
        this.stars = {};
    }

    ////////// BLACKHOLE
    blackHoles: Object = {};
    addBlackHole(blackHole: BlackHole) {
        this.blackHoles[blackHole.key] = blackHole;
    }

    removeBlackHole(blackHole: BlackHole) {
        delete this.blackHoles[blackHole.key];
    }

    disposeBlackHole(blackHole: BlackHole) {
        this.removeBlackHole(blackHole);
        blackHole.transformMesh.dispose();
    }

    eraseAllBlackHoles() {
        for (const key in this.blackHoles) {
            this.blackHoles[key].dispose();
        }
        this.blackHoles = {};
    }

    ////////// DUST
    checkPlayerDust(player: Player) {
        for (let i = 0; i < this.dusts.length; i++) {
            const dust = this.dusts[i];
            let dist = Vector2.Distance(dust.position, player.position);
            if (dist < player.gravityField) {
                this.moveDustToPlayer(player, dust);
            }
        }
    }

    moveDustToPlayer(player: Player, dust: StarDust) {
        this.removeDust(dust);
        dust.goToEntity(player, () => {
            player.addDust();
            this.storageDust(dust);
        });
    }

    checkPlayersDust() {
        for (const key in this.stars) {
            const player: Player = this.stars[key];
            if (!player.accelerating && player.isStarVisible) this.checkPlayerDust(player);
        }
    }

    checkPlayersCycle() {
        for (const key in this.stars) {
            const player: Player = this.stars[key];
            if (player.isStarVisible) {
                player.satelliteCycle();
            }
        }
    }

    killPlayer(player: Player) {
        this.removeStar(player);
        player.die(() => {
            if (player.dustField) this.addDustField(player.position);
        });
    }

    eraseAllEntity() {
        // Erase Players before Planets so that planet are all in storage
        this.eraseAllStars();
        this.eraseAllBlackHoles();
        this.eraseAllDusts();
        this.eraseAllPlanets();
        this.system.checkActiveMeshes();
        this.addStar(this.playerToFollow);
    }
}